// La celda de crédito de una fila del listado, ahora que las cifras llegan resueltas por el BFF —
// lógica PURA, sin React y sin fetch.
//
// El listado NO distingue la baja contable de la cancelación: el BFF no publica
// `writtenOffCreditsCount`. Por eso este archivo tiene su propio resolvedor de estado en vez de
// reusar el de la cartera por lote, que sí lo mira.
import type { CreditCustomerCredit, CreditCustomerListItem } from '@/api/services/loan-credit';
import type { CustomerCreditStatus } from './creditCustomersFormat';

/** Por qué una fila no trae cifras de crédito. Los dos piden cosas distintas de quien los lee. */
export type CreditRowGap = 'no-borrower' | 'unavailable';

/** Lo que se pinta en la celda sin cifras. Nunca un cero, que se leería como «no debe nada». */
export const CREDIT_ROW_GAP_LABEL: Record<CreditRowGap, string> = {
    'no-borrower': 'Sin acreditado',
    unavailable: 'No se pudo leer',
};

export const CREDIT_ROW_GAP_NOTE: Record<CreditRowGap, string> = {
    'no-borrower':
        'Este cliente todavía no tiene un acreditado en el silo de crédito, así que no hay SCI, nivel ni saldo que mostrar. No es un dato que falte: no existe.',
    unavailable:
        'No se pudo leer la cartera de crédito de este cliente. Un 0 aquí afirmaría que no debe nada; vuelve a cargar la lista.',
};

/** Las cifras del cliente, o el motivo por el que no las hay. Nunca las dos cosas a la vez. */
export type CreditRowFacts =
    | { credit: CreditCustomerCredit; gap: null }
    | { credit: null; gap: CreditRowGap };

/** Qué le toca pintar a la celda: las cifras leídas, o el motivo del hueco. */
export function creditRowFactsOf(credit: CreditCustomerCredit): CreditRowFacts {
    if (credit.state === 'READ') return { credit, gap: null };
    return { credit: null, gap: credit.state === 'NO_BORROWER' ? 'no-borrower' : 'unavailable' };
}

/** ¿Tiene al menos un crédito vivo? Es la pastilla «Con activos». */
export function hasActiveCredit(credit: CreditCustomerCredit): boolean {
    return credit.state === 'READ' && credit.activeCreditsCount > 0;
}

/** ¿Tiene al menos una cuota vencida sin pagar? Es la pastilla «En mora». */
export function isInArrears(credit: CreditCustomerCredit): boolean {
    return credit.state === 'READ' && (credit.arrearsDays ?? 0) > 0;
}

/**
 * El estado operativo del cliente, sobre lo que el BFF publica. `WRITTEN_OFF` no sale de aquí: el
 * listado no trae las bajas contables y afirmarlas sin el dato sería inventar.
 */
export type CreditRowStatus = Exclude<CustomerCreditStatus, 'WRITTEN_OFF'>;

export function creditRowStatusOf(credit: CreditCustomerCredit): CreditRowStatus {
    if (credit.creditsCount === 0) return 'NO_CREDITS';
    if (isInArrears(credit)) return 'IN_ARREARS';
    if (hasActiveCredit(credit)) return 'CURRENT';
    if (credit.settledCreditsCount > 0) return 'SETTLED';
    return 'CANCELLED';
}

/**
 * Qué significa «Sin vigentes» en ESTE listado. La nota general dice que el crédito se canceló, y
 * aquí no se puede afirmar: sin las bajas contables, un deudor castigado cae en el mismo cajón.
 */
export const CANCELLED_ROW_NOTE =
    'No tiene crédito vigente ni ninguno liquidado. El listado no distingue la cancelación de la baja contable: para saber cuál fue, abre su ficha.';

/** Severidad del peor al mejor: es el orden operativo de la columna Estado. */
const STATUS_SEVERITY: Record<CreditRowStatus, number> = {
    IN_ARREARS: 0,
    CURRENT: 1,
    SETTLED: 2,
    CANCELLED: 3,
    NO_CREDITS: 4,
};

/** Separación entre tramos, para que los días de mora nunca crucen de severidad. */
const SEVERITY_STEP = 100_000;

/** Severidad primero y, dentro de «en mora», más días vencidos primero. */
export function creditRowStatusSortValue(credit: CreditCustomerCredit): number {
    return STATUS_SEVERITY[creditRowStatusOf(credit)] * SEVERITY_STEP - (credit.arrearsDays ?? 0);
}

export interface CreditRowCounts {
    active: number;
    arrears: number;
}

/** Las dos pastillas de la barra, contadas sobre las filas que se le pasen. */
export function creditRowCounts(rows: readonly CreditCustomerListItem[]): CreditRowCounts {
    let active = 0;
    let arrears = 0;
    for (const row of rows) {
        if (hasActiveCredit(row.credit)) active += 1;
        if (isInArrears(row.credit)) arrears += 1;
    }
    return { active, arrears };
}
